<template>
  <div class="bpp-summary">
    <div
      role="status"
      class="sr-only"
      aria-live="polite"
    >
      {{ screenReaderMessage }}
    </div>

    <section class="bpp-summary__top">
      <div class="px-6 pt-8 md:px-16 xl:px-32">
        <button
          v-if="data.estimatedCosts && data.estimatedCosts.restartButtonText"
          type="button"
          class="flex items-center gap-1 text-sm font-bold uppercase tracking-0.5px text-black-high-emphasis"
          data-testid="restart-quiz"
          @click="onRestart"
        >
          <KIcon name="keyboard-arrow-left" class="h-3 w-2" />
          {{ data.estimatedCosts.restartButtonText }}
        </button>
      </div>

      <div
        class="px-10 pt-4 md:px-16 text-center"
      >
        <h3
          v-if="data.estimatedCosts && data.estimatedCosts.estimateType"
          class="text-2xl md:text-34 font-bold text-primary mb-8"
          data-testid="summary-heading"
        >
          {{data.estimatedCosts.estimateType}}
        </h3>
      </div>

      <SelectedChoiceSummary
        :selections="topSelections"
        :edit-selections-text="data.estimatedCosts && data.estimatedCosts.editButtonText"
        @edit-selections="onEditSelections"
      />
      <EstimatedCosts v-if="data.estimatedCosts" :content="data.estimatedCosts" />
    </section>

    <section
      v-if="middleSection.content"
      class="bpp-summary__middle -mt-6 md:-mt-10 bg-background-light"
    >
      <component
        :is="middleSection.component"
        :content="middleSection.content"
      />
    </section>

    <BottomSummary class="bpp-summary__bottom" :cards="data.bottomSummary" />
  </div>
</template>

<script lang="ts">
import { useGlobal, useFlow } from '@/store'
import { defineComponent, computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'

import { useRoute, useRouter } from 'vue-router'
import {
  EstimatedCosts,
  MemberDiscounts,
  PriceComparison,
  BottomSummary,
  SelectedChoiceSummary,
  KIcon
} from '@/components'
import { Selection } from '@/model'
import { trackEvent } from '@/utils/analytics'
import { trackSelectionEventCJA, addAdobeDataLayerEventEditSelection } from '@/utils/adobe-analytics-data-layer'

export default defineComponent({
  name: 'Final',
  components: {
    EstimatedCosts,
    SelectedChoiceSummary,
    BottomSummary,
    KIcon
  },
  setup () {
    const route = useRoute()
    const router = useRouter()
    const global = useGlobal()
    const { finalPageData: data, showEye360 } = storeToRefs(global)

    const flowStore = useFlow()
    const { selectedAnswers, flow } = storeToRefs(flowStore)

    const onRestart = () => {
      trackEvent('event-restart')
      trackSelectionEventCJA('event-restart')
      flowStore.startFlow()
      router.replace({ path: 'flow', query: route.query })
    }

    const onEditSelections = () => {
      addAdobeDataLayerEventEditSelection()
      flowStore.startFlow()
      router.replace({ path: 'flow', query: route.query })
    }

    const topSelections = computed(() => {
      return Object.entries(selectedAnswers.value)
        .reduce<Selection[]>((selections, [, answer]) => [
          ...selections,
          ...answer.selections
        ], [])
        .filter((selection) => !!selection.procedureCode)
    })

    const screenReaderMessage = ref('')

    const isContentLoaded = computed(() => {
      return !!data.value?.estimatedCosts && Object.keys(data.value.estimatedCosts).length > 0
    })

    onMounted(() => {
      const checkForData = setInterval(() => {
        if (isContentLoaded.value && !screenReaderMessage.value) {
          screenReaderMessage.value = 'Estimated out of pocket costs have been loaded.'
          clearInterval(checkForData)
        }
      }, 100)

      return () => clearInterval(checkForData)
    })

    const middleSection = computed(() => (
      showEye360.value
        ? { component: PriceComparison, content: data.value.priceComparison }
        : { component: MemberDiscounts, content: data.value.memberDiscounts }
    ))

    trackEvent('event-price-display')
    trackSelectionEventCJA('event-price-display')

    return {
      data,
      middleSection,
      topSelections,
      onRestart,
      onEditSelections,
      screenReaderMessage
    }
  }
})
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

</style>
